/**
 * Bundle Box — Currency Converter
 * Runs on any page containing a .bundle-box (all Offer Bundle Box / cw-* PDP templates),
 * except where the buy box's gift prices were typed in the market's own currency.
 *
 * A market-specific "*.context.<market>.json" template that sets its own gift prices
 * (e.g. "€9", "£17") also sets the block's "gift_prices_localized" checkbox, rendered as
 * data-prices-localized="true" in snippets/product-offer-bundle.liquid. Those amounts are
 * final, so they are left untouched. Every other template/market combination shows the
 * USD-declared values, converted here to the visitor's currency (2026-10-07: this used to
 * be decided by market handle, so the 80+ templates without market copies showed raw USD
 * numbers in the UK, eurozone, Canada, Australia and New Zealand).
 * Converted gift values are rounded to whole units, like the hand-entered market prices, and
 * everything is formatted with the market's money format (theme.settings.moneyFormat).
 *
 * Converts:
 *   - .bundle-box__price-old on every gift item after the first (main product is item 1)
 *
 * Reads each item's declared USD value directly from its rendered text (whatever
 * is set in the block's settings), so gift names/values stay correct per-product —
 * this never hardcodes a price, it only converts whatever is actually declared.
 *
 * .bundle-box__original-price (the bundle's declared total compare-at price) is
 * NOT converted from a separately-declared value — it's recomputed as the sum of
 * every .bundle-box__price-old currently on display (main product's real Shopify
 * price + each already-converted/pre-localized gift price), so it can never drift
 * out of sync with the individual line items, in whatever currency they're in.
 *
 * Leaves untouched:
 *   - Main product prices (item 1) — Shopify already handles those
 *   - All FREE values
 *   - .bundle-box__total-price — Shopify already handles that
 */

(function () {
  'use strict';

  function parseUSDFloat(text) {
    const trimmed = (text || '').trim();
    if (/^free$/i.test(trimmed)) return null;

    // Normalize the decimal separator: treat the LAST comma/period found as the
    // decimal point (handles both "1,234.56" and "1.234,56"/"9,95" styles), so a
    // value like "201,95" parses as 201.95 instead of collapsing into 20195.
    const kept = trimmed.replace(/[^0-9.,]/g, '');
    if (!kept) return null;
    const lastSep = Math.max(kept.lastIndexOf(','), kept.lastIndexOf('.'));
    let numeric;
    if (lastSep === -1) {
      numeric = kept;
    } else {
      const whole = kept.slice(0, lastSep).replace(/[.,]/g, '');
      const frac = kept.slice(lastSep + 1).replace(/[.,]/g, '');
      numeric = frac.length ? `${whole}.${frac}` : whole;
    }
    const val = parseFloat(numeric);
    return isNaN(val) ? null : val;
  }

  function formatWithDelimiters(amount, decimals, thousands, decimal) {
    var parts = amount.toFixed(decimals).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
  }

  // Format like Shopify's own prices for the visitor's market (e.g. "€8,95", "£8.00", "$14.00")
  function formatCurrency(amount, code) {
    // US keeps the plain "$10.00" look (its money format adds " USD" and HTML)
    var fmt = code !== 'USD' && window.theme && window.theme.settings && window.theme.settings.moneyFormat;
    if (fmt) fmt = fmt.replace(/<[^>]*>/g, '').trim();
    if (fmt && /\{\{\s*\w+\s*\}\}/.test(fmt)) {
      return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
        switch (key) {
          case 'amount_no_decimals': return formatWithDelimiters(amount, 0, ',', '.');
          case 'amount_with_comma_separator': return formatWithDelimiters(amount, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return formatWithDelimiters(amount, 0, '.', ',');
          case 'amount_with_apostrophe_separator': return formatWithDelimiters(amount, 2, "'", '.');
          case 'amount_with_space_separator': return formatWithDelimiters(amount, 2, ' ', ',');
          default: return formatWithDelimiters(amount, 2, ',', '.');
        }
      });
    }
    try {
      return new Intl.NumberFormat('en', {
        style: 'currency',
        currency: code,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (_) {
      return code + '\u00a0' + amount.toFixed(2);
    }
  }

  function getMarketCurrency() {
    return new Promise((resolve) => {
      const sc = window.Shopify?.currency;
      if (sc?.active && sc?.rate) {
        resolve({ active: sc.active, rate: parseFloat(sc.rate) });
        return;
      }
      fetch('/cart.js')
        .then((r) => r.json())
        .then((c) => resolve({ active: c.currency || 'USD', rate: 1 }))
        .catch(() => resolve({ active: 'USD', rate: 1 }));
    });
  }

  async function run() {
    const bundleBox = document.querySelector('.bundle-box');
    if (!bundleBox) return;

    const { active, rate } = await getMarketCurrency();

    // Markets with their own pre-localized template already declare correct,
    // final per-market amounts — skip converting those, but still recompute
    // the total below from whatever is actually displayed.
    const isPreLocalized = bundleBox.dataset.pricesLocalized === 'true';

    if (!isPreLocalized) {
      // ── 1. Gift items: items 2,3,4,5 (index 1–4) → convert price-old only ──
      const allItems = bundleBox.querySelectorAll('.bundle-box__item');

      allItems.forEach((item, index) => {
        // Skip first item (main product — Shopify handles it)
        if (index === 0) return;

        const priceOld = item.querySelector('.bundle-box__price-old');
        if (!priceOld) return;
        if (priceOld.dataset.currencyDone === active) return;

        // Store original USD once
        if (!priceOld.dataset.usd) {
          const usd = parseUSDFloat(priceOld.textContent);
          if (usd === null) return;
          priceOld.dataset.usd = usd.toString();
        }

        const converted = rate === 1 ? parseFloat(priceOld.dataset.usd) : Math.round(parseFloat(priceOld.dataset.usd) * rate);
        priceOld.textContent = formatCurrency(converted, active);
        priceOld.dataset.currencyDone = active;
      });
    }

    // ── 1b. Same number style everywhere (weekly QA 2026-10-10 F8/F9) ──
    // Pre-localized market templates type gift values by hand ("€8.95") while Shopify shows "€145,95": reformat them in
    // the market's own format (no conversion, the amounts are already in that currency).
    if (isPreLocalized && active && active !== 'USD') {
      bundleBox.querySelectorAll('.bundle-box__item').forEach((item, index) => {
        if (index === 0) return;
        const priceOld = item.querySelector('.bundle-box__price-old');
        if (!priceOld || priceOld.dataset.formatDone === active) return;
        const val = parseUSDFloat(priceOld.textContent);
        if (val === null) return;
        priceOld.textContent = formatCurrency(val, active);
        priceOld.dataset.formatDone = active;
      });
    }
    // Gift names typed in USD ("$50 Gift Card") show the shopper's currency outside the US.
    if (active && active !== 'USD') {
      bundleBox.querySelectorAll('.bundle-box__name').forEach((el) => {
        if (el.dataset.nameDone === active) return;
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
        let node;
        while ((node = walker.nextNode())) {
          if (!/\$\s?\d/.test(node.nodeValue)) continue;
          node.nodeValue = node.nodeValue.replace(/\$\s?(\d+(?:[.,]\d+)?)/g, (_, n) => {
            const usd = parseFloat(n.replace(',', '.'));
            const local = rate === 1 ? usd : Math.round(usd * rate);
            return formatCurrency(local, active).replace(/[.,]00(?=\D*$)/, '');
          });
        }
        el.dataset.nameDone = active;
      });
    }

    // ── 2. Total compare_at — recomputed as the sum of every .bundle-box__price-old
    // currently on display (main product's real Shopify price + each gift price,
    // already in the correct currency from step 1 or from the pre-localized
    // template). Never converts a separately-declared value, so it can't drift
    // out of sync or get double-converted. ──
    const originalPrice = bundleBox.querySelector('.bundle-box__original-price');
    if (!originalPrice) { syncBundleOffer(bundleBox); return; }

    let sum = 0;
    let hasAny = false;
    bundleBox.querySelectorAll('.bundle-box__price-old').forEach((el) => {
      const val = parseUSDFloat(el.textContent);
      if (val !== null) {
        sum += val;
        hasAny = true;
      }
    });

    if (hasAny) {
      const displayCurrency = active || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      originalPrice.textContent = formatCurrency(sum, displayCurrency);
      delete originalPrice.dataset.usd;
      delete originalPrice.dataset.currencyDone;
    }
    syncBundleOffer(bundleBox);
  }

  // ── 3. The "Ready to improve…" bottom offer (sections/cw-bundle-offer) shows the same gifts again. Its Liquid
  // guesses the exchange rate from a typed USD price, which goes stale whenever a price changes (2026-10-23: F10
  // and the 2.2L showed CA $11.34 / $9.47 for a $10 gift while the buy box said $14). Copy the buy box's final gift
  // values and total so both always agree, in every market (only when both list the same number of gifts). ──
  function syncBundleOffer(bundleBox) {
    const offer = document.querySelector('.bundle-offer');
    if (!offer || !bundleBox) return;
    const boxGifts = Array.from(bundleBox.querySelectorAll('.bundle-box__item')).slice(1)
      .map((item) => item.querySelector('.bundle-box__price-old')).filter(Boolean);
    const offerGifts = Array.from(offer.querySelectorAll('.bundle-offer__row')).slice(1)
      .map((row) => row.querySelector('.bundle-offer__old-price')).filter(Boolean);
    if (!boxGifts.length || boxGifts.length !== offerGifts.length) return;
    // same gifts in the same order? (names share a word, e.g. "Free Shipping" / "Free Shipping", "2 Cat Toys" / "Cat Toys")
    const words = (t) => new Set((t || '').toLowerCase().replace(/[^a-z ]/g, ' ').split(/s+/).filter((w) => w.length > 3 && w !== 'free'));
    const boxNames = Array.from(bundleBox.querySelectorAll('.bundle-box__item')).slice(1).map((i) => (i.querySelector('.bundle-box__name') || i).textContent);
    const offerNames = Array.from(offer.querySelectorAll('.bundle-offer__row')).slice(1).map((r) => (r.querySelector('.bundle-offer__item-name') || r).textContent);
    const sameGifts = boxNames.every((n, i) => { const a = words(n), b = words(offerNames[i]); return [...a].some((w) => b.has(w)); });
    // or: every bottom value is the same multiple of its buy box value (same gifts, same order, only converted
    // at a stale rate), e.g. the 2.2L labels the toys "Free Gifts" where the buy box says "2 Cat Toys" (2026-10-23)
    const ratios = boxGifts.map((el, i) => { const b = parseUSDFloat(el.textContent), o = parseUSDFloat(offerGifts[i].textContent); return b && o ? o / b : null; });
    const sameScale = ratios.every((r) => r !== null) && ratios.every((r) => Math.abs(r - ratios[0]) <= 0.10 * ratios[0]);
    if (!sameGifts && !sameScale) return;
    offerGifts.forEach((el, i) => { if (el.textContent !== boxGifts[i].textContent) el.textContent = boxGifts[i].textContent; });
    const boxTotal = bundleBox.querySelector('.bundle-box__original-price');
    const offerTotal = offer.querySelector('.bundle-offer__total-old');
    if (boxTotal && offerTotal && offerTotal.textContent !== boxTotal.textContent) offerTotal.textContent = boxTotal.textContent;
  }

  // ── "Add to Cart — Save NN%" follows the buy box badge ──────────────────────
  // The button text is typed per template from the USD prices, while the badge is computed from the
  // prices in the visitor's currency, so they could differ by 1% (e.g. SAVE 39% vs Save 40% in GBP).
  function syncSaveButtons() {
    const badge = document.querySelector('.bundle-box .bundle-box__save-badge');
    const m = badge && badge.textContent.match(/(\d+)\s*%/);
    if (!m) return;
    document.querySelectorAll('[data-add-to-cart-text]').forEach((el) => {
      const swap = (t) => t.replace(/(save\s+)\d+(\s*%)/i, (_, a, b) => a + m[1] + b);
      if (/save\s+\d+\s*%/i.test(el.textContent)) {
        const next = swap(el.textContent);
        if (next !== el.textContent) el.textContent = next;
      }
      const d = el.getAttribute('data-default-text');
      if (d && /save\s+\d+\s*%/i.test(d)) el.setAttribute('data-default-text', swap(d));
    });
  }

  function watchSaveBadge() {
    syncSaveButtons();
    const badge = document.querySelector('.bundle-box .bundle-box__save-badge');
    if (badge && window.MutationObserver) {
      new MutationObserver(syncSaveButtons).observe(badge, { childList: true, characterData: true, subtree: true });
    }
    // the button text can be re-rendered on variant/plan change
    document.addEventListener('change', () => setTimeout(syncSaveButtons, 50), true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchSaveBadge);
  } else {
    watchSaveBadge();
  }

  // ── Currency switcher support ─────────────────────────────────────────────
  function resetAndRun() {
    document.querySelectorAll('.bundle-box [data-currency-done]').forEach((el) => {
      delete el.dataset.currencyDone;
    });
    run();
  }

  document.addEventListener('currency:changed', resetAndRun);

  let _last = window.Shopify?.currency?.active;
  setInterval(() => {
    const cur = window.Shopify?.currency?.active;
    if (cur && cur !== _last) { _last = cur; resetAndRun(); }
  }, 800);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

})();